import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  ServiceInstance,
  CreateInstanceRequest,
  InstanceLogsResponse,
} from "../types";
import { instanceService, MAX_ACTIVE_INSTANCES } from "../services";

interface InstancesState {
  instances: ServiceInstance[];
  activeInstances: ServiceInstance[];
  selectedInstance: ServiceInstance | null;
  activeCount: number;
  totalElements: number;
  totalPages: number;
  currentPage: number;
  isLoading: boolean;
  isCreating: boolean;
  isTerminating: boolean;
  isStopping: boolean;
  isStarting: boolean;
  isRestarting: boolean;
  logs: InstanceLogsResponse | null;
  isLoadingLogs: boolean;
  error: string | null;
}

const initialState: InstancesState = {
  instances: [],
  activeInstances: [],
  selectedInstance: null,
  activeCount: 0,
  totalElements: 0,
  totalPages: 0,
  currentPage: 0,
  isLoading: false,
  isCreating: false,
  isTerminating: false,
  isStopping: false,
  isStarting: false,
  isRestarting: false,
  logs: null,
  isLoadingLogs: false,
  error: null,
};

export const fetchUserInstances = createAsyncThunk<
  ServiceInstance[],
  { userId: number; page?: number; size?: number },
  { rejectValue: string }
>(
  "instances/fetchByUser",
  async ({ userId, page = 0, size = 20 }, { rejectWithValue }) => {
    try {
      const response = await instanceService.getByUser(userId, page, size);
      return response;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch instances",
      );
    }
  },
);

export const fetchActiveInstances = createAsyncThunk<
  ServiceInstance[],
  number,
  { rejectValue: string }
>("instances/fetchActive", async (userId, { rejectWithValue }) => {
  try {
    const response = await instanceService.getActiveByUser(userId);
    return response;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch active instances",
    );
  }
});

export const fetchActiveCount = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("instances/fetchActiveCount", async (userId, { rejectWithValue }) => {
  try {
    const count = await instanceService.getActiveCount(userId);
    return count;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch active count",
    );
  }
});

export const fetchInstanceById = createAsyncThunk<
  ServiceInstance,
  number,
  { rejectValue: string }
>("instances/fetchById", async (id, { rejectWithValue }) => {
  try {
    const response = await instanceService.getById(id);
    return response;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch instance",
    );
  }
});

export const fetchInstanceByInstanceId = createAsyncThunk<
  ServiceInstance,
  string,
  { rejectValue: string }
>("instances/fetchByInstanceId", async (instanceId, { rejectWithValue }) => {
  try {
    const response = await instanceService.getByInstanceId(instanceId);
    return response;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch instance",
    );
  }
});

export const createInstance = createAsyncThunk<
  ServiceInstance,
  CreateInstanceRequest,
  { rejectValue: string }
>("instances/create", async (request, { rejectWithValue }) => {
  try {
    const response = await instanceService.create(request);
    return response;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    return rejectWithValue(
      err.response?.data?.message || "Failed to create instance",
    );
  }
});

export const terminateInstance = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("instances/terminate", async (instanceId, { rejectWithValue }) => {
  try {
    await instanceService.terminate(instanceId);
    return instanceId;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    return rejectWithValue(
      err.response?.data?.message || "Failed to terminate instance",
    );
  }
});

export const stopInstance = createAsyncThunk<
  ServiceInstance,
  string,
  { rejectValue: string }
>("instances/stop", async (instanceId, { rejectWithValue }) => {
  try {
    const response = await instanceService.stop(instanceId);
    return response;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    return rejectWithValue(
      err.response?.data?.message || "Failed to stop instance",
    );
  }
});

export const startInstance = createAsyncThunk<
  ServiceInstance,
  string,
  { rejectValue: string }
>("instances/start", async (instanceId, { rejectWithValue }) => {
  try {
    const response = await instanceService.start(instanceId);
    return response;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    return rejectWithValue(
      err.response?.data?.message || "Failed to start instance",
    );
  }
});

export const restartInstance = createAsyncThunk<
  ServiceInstance,
  string,
  { rejectValue: string }
>("instances/restart", async (instanceId, { rejectWithValue }) => {
  try {
    const response = await instanceService.restart(instanceId);
    return response;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    return rejectWithValue(
      err.response?.data?.message || "Failed to restart instance",
    );
  }
});

export const fetchInstanceLogs = createAsyncThunk<
  InstanceLogsResponse,
  { instanceId: string; lines?: number },
  { rejectValue: string }
>(
  "instances/fetchLogs",
  async ({ instanceId, lines = 100 }, { rejectWithValue }) => {
    try {
      const response = await instanceService.getLogs(instanceId, lines);
      return response;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch logs",
      );
    }
  },
);

const instancesSlice = createSlice({
  name: "instances",
  initialState,
  reducers: {
    setSelectedInstance: (
      state,
      action: PayloadAction<ServiceInstance | null>,
    ) => {
      state.selectedInstance = action.payload;
    },
    clearInstancesError: (state) => {
      state.error = null;
    },
    updateInstanceStatus: (
      state,
      action: PayloadAction<{
        instanceId: string;
        status: ServiceInstance["status"];
      }>,
    ) => {
      const { instanceId, status } = action.payload;
      const instance = state.instances.find((i) => i.instanceId === instanceId);
      if (instance) {
        instance.status = status;
      }
      const activeInstance = state.activeInstances.find(
        (i) => i.instanceId === instanceId,
      );
      if (activeInstance) {
        activeInstance.status = status;
      }
      if (state.selectedInstance?.instanceId === instanceId) {
        state.selectedInstance.status = status;
      }
    },
    clearLogs: (state) => {
      state.logs = null;
    },
    removeInstance: (state, action: PayloadAction<string>) => {
      const instanceId = action.payload;
      state.instances = state.instances.filter(
        (i) => i.instanceId !== instanceId,
      );
      state.activeInstances = state.activeInstances.filter(
        (i) => i.instanceId !== instanceId,
      );
      state.totalElements = state.instances.length;
      if (state.selectedInstance?.instanceId === instanceId) {
        state.selectedInstance = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch user instances
      .addCase(fetchUserInstances.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserInstances.fulfilled, (state, action) => {
        state.isLoading = false;
        state.instances = action.payload;
        state.totalElements = action.payload.length;
      })
      .addCase(fetchUserInstances.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch instances";
      })
      // Fetch active instances
      .addCase(fetchActiveInstances.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchActiveInstances.fulfilled, (state, action) => {
        state.isLoading = false;
        state.activeInstances = action.payload;
        state.activeCount = action.payload.length;
        // Sync updated statuses back into the full instances list
        for (const active of action.payload) {
          const idx = state.instances.findIndex(
            (i) => i.instanceId === active.instanceId,
          );
          if (idx !== -1) {
            state.instances[idx] = active;
          }
        }
      })
      .addCase(fetchActiveInstances.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch active instances";
      })
      // Fetch active count
      .addCase(fetchActiveCount.fulfilled, (state, action) => {
        state.activeCount = action.payload;
      })
      // Fetch instance by ID
      .addCase(fetchInstanceById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInstanceById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedInstance = action.payload;
      })
      .addCase(fetchInstanceById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch instance";
      })
      // Fetch instance by instanceId
      .addCase(fetchInstanceByInstanceId.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInstanceByInstanceId.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedInstance = action.payload;
      })
      .addCase(fetchInstanceByInstanceId.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch instance";
      })
      // Create instance
      .addCase(createInstance.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createInstance.fulfilled, (state, action) => {
        state.isCreating = false;
        state.instances.unshift(action.payload);
        state.activeInstances.push(action.payload);
        state.activeCount += 1;
      })
      .addCase(createInstance.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload || "Failed to create instance";
      })
      // Terminate instance
      .addCase(terminateInstance.pending, (state) => {
        state.isTerminating = true;
        state.error = null;
      })
      .addCase(terminateInstance.fulfilled, (state, action) => {
        state.isTerminating = false;
        const instanceId = action.payload;
        // Update status in instances list
        const idx = state.instances.findIndex(
          (i) => i.instanceId === instanceId,
        );
        if (idx !== -1) {
          state.instances[idx].status = "TERMINATED";
          state.instances[idx].terminatedAt = new Date().toISOString();
        }
        // Remove from active instances
        state.activeInstances = state.activeInstances.filter(
          (i) => i.instanceId !== instanceId,
        );
        state.activeCount = Math.max(0, state.activeCount - 1);
        // Update selected if it's the same
        if (state.selectedInstance?.instanceId === instanceId) {
          state.selectedInstance.status = "TERMINATED";
          state.selectedInstance.terminatedAt = new Date().toISOString();
        }
      })
      .addCase(terminateInstance.rejected, (state, action) => {
        state.isTerminating = false;
        state.error = action.payload || "Failed to terminate instance";
      })
      // Stop instance
      .addCase(stopInstance.pending, (state) => {
        state.isStopping = true;
        state.error = null;
      })
      .addCase(stopInstance.fulfilled, (state, action) => {
        state.isStopping = false;
        const instanceId = action.payload.instanceId;
        const idx = state.instances.findIndex(
          (i) => i.instanceId === instanceId,
        );
        if (idx !== -1) {
          state.instances[idx] = action.payload;
        }
        const activeIdx = state.activeInstances.findIndex(
          (i) => i.instanceId === instanceId,
        );
        if (activeIdx !== -1) {
          state.activeInstances[activeIdx] = action.payload;
        }
        if (state.selectedInstance?.instanceId === instanceId) {
          state.selectedInstance = action.payload;
        }
      })
      .addCase(stopInstance.rejected, (state, action) => {
        state.isStopping = false;
        state.error = action.payload || "Failed to stop instance";
      })
      // Start instance
      .addCase(startInstance.pending, (state) => {
        state.isStarting = true;
        state.error = null;
      })
      .addCase(startInstance.fulfilled, (state, action) => {
        state.isStarting = false;
        const instanceId = action.payload.instanceId;
        const idx = state.instances.findIndex(
          (i) => i.instanceId === instanceId,
        );
        if (idx !== -1) {
          state.instances[idx] = action.payload;
        }
        const activeIdx = state.activeInstances.findIndex(
          (i) => i.instanceId === instanceId,
        );
        if (activeIdx !== -1) {
          state.activeInstances[activeIdx] = action.payload;
        }
        if (state.selectedInstance?.instanceId === instanceId) {
          state.selectedInstance = action.payload;
        }
      })
      .addCase(startInstance.rejected, (state, action) => {
        state.isStarting = false;
        state.error = action.payload || "Failed to start instance";
      })
      // Restart instance
      .addCase(restartInstance.pending, (state) => {
        state.isRestarting = true;
        state.error = null;
      })
      .addCase(restartInstance.fulfilled, (state, action) => {
        state.isRestarting = false;
        const instanceId = action.payload.instanceId;
        const idx = state.instances.findIndex(
          (i) => i.instanceId === instanceId,
        );
        if (idx !== -1) {
          state.instances[idx] = action.payload;
        }
        const activeIdx = state.activeInstances.findIndex(
          (i) => i.instanceId === instanceId,
        );
        if (activeIdx !== -1) {
          state.activeInstances[activeIdx] = action.payload;
        }
        if (state.selectedInstance?.instanceId === instanceId) {
          state.selectedInstance = action.payload;
        }
      })
      .addCase(restartInstance.rejected, (state, action) => {
        state.isRestarting = false;
        state.error = action.payload || "Failed to restart instance";
      })
      // Fetch logs
      .addCase(fetchInstanceLogs.pending, (state) => {
        state.isLoadingLogs = true;
        state.error = null;
      })
      .addCase(fetchInstanceLogs.fulfilled, (state, action) => {
        state.isLoadingLogs = false;
        state.logs = action.payload;
      })
      .addCase(fetchInstanceLogs.rejected, (state, action) => {
        state.isLoadingLogs = false;
        state.error = action.payload || "Failed to fetch logs";
      });
  },
});

export const {
  setSelectedInstance,
  clearInstancesError,
  updateInstanceStatus,
  clearLogs,
  removeInstance,
} = instancesSlice.actions;
export const selectCanCreateInstance = (state: { instances: InstancesState }) =>
  state.instances.activeCount < MAX_ACTIVE_INSTANCES;
export default instancesSlice.reducer;
